import React from 'react';
import { observer } from 'mobx-react';
import { EventHubsListVM } from './event-hubs-list.viewmodel';
import { EVENT_HUB_COLUMN_CREATED_DATE_TEXT, EVENT_HUB_COLUMN_NAME_TEXT, EVENT_HUBS_TITLE_TEXT, NEW_HUB_TEXT, NO_EVENT_HUBS_MESSAGE_TEXT } from '../event-hubs.text';
import { ITheadSort, ListLayout } from '../../global/tables/table-list-layout';
import DefaultButton from '../../global/default-button/default-button';
import { Permission } from '../../../api/roles.dao';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { EventHubsListItem } from './event-hubs-list-item/event-hubs-list-item';
import TableLoadingRow from '../../global/tables/table-loading-row';
import { SubWindowsHost } from '../../global/sub-window/sub-windows-host';

export type EventHubsListPresentationProps = {
    vm: EventHubsListVM;
};

export const EventHubsListPresentation = observer(({ vm }: EventHubsListPresentationProps) => {
    if (vm.loadingAccountDetails) return null;

    const accountId = vm.getAccountId();

    const theads: ITheadSort<string>[] = [
        {
            title: vm.intl.formatMessage(EVENT_HUB_COLUMN_NAME_TEXT),
        },
        {
            title: vm.intl.formatMessage(EVENT_HUB_COLUMN_CREATED_DATE_TEXT),
        },
        {
            width: '20%'
        }
    ];

    const isEmpty = !vm.loadingEventHubs && !vm.getEventHubsList().length;

    const hasCreateHubPermission = hasPermission(Permission.CREATE_EVENT_HUB);
    const hasEditHubPermission = hasPermission(Permission.EDIT_EVENT_HUB);

    return (
        <SubWindowsHost>
            <div className='page-container'>
                <section className='top-page-title horizontal-row horizontal-row--with-bottom-margin'>
                    <h1 className='text-ellipsis'>{vm.intl.formatMessage(EVENT_HUBS_TITLE_TEXT, { prefix: `${vm.getAccountName()} ` })}</h1>
                    <div className='horizontal-row__align-to-row-end'>
                        <div className='buttons-block'>
                            {hasCreateHubPermission &&
                                <DefaultButton
                                    className='events-list__button-new'
                                    modifier='fat'
                                    text={vm.intl.formatMessage(NEW_HUB_TEXT, { prefix: '+ ' })}
                                    onClick={vm.openCreateHubForm}
                                />
                            }
                        </div>
                    </div>
                </section>
                <section className='events-list fixed-list-layout'>
                    <ListLayout
                        thead={theads}
                        sortOptions={{ theads: theads }}
                    >
                        {
                            !vm.loadingEventHubs && vm.getEventHubsList().map(hub => {
                                return (
                                    <EventHubsListItem
                                        key={hub.eventHubId}
                                        accountId={accountId}
                                        hasEditPermission={hasEditHubPermission}
                                        isSelected={vm.isEditHubSubwindowShown(hub.eventHubId)}
                                        eventHub={hub}
                                        onDoubleClick={() => vm.navigateToHubConfiguration(hub.eventHubId)}
                                        onClickEdit={() => vm.openEditHubForm(hub.eventHubId)}
                                    />
                                );
                            })
                        }
                        { vm.loadingEventHubs && <TableLoadingRow colSpan={theads.length} /> }
                    </ListLayout>
                    {isEmpty && (
                        <div className='empty-list-message'>
                            {vm.intl.formatMessage(NO_EVENT_HUBS_MESSAGE_TEXT)}
                        </div>
                    )}
                </section>
            </div>
        </SubWindowsHost>
    );
});